import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import {
  can,
  formatQty,
  type VanTemplateProposal,
  type VanTemplatePreviewInput,
} from '@servwell/shared';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { fieldErrorOf } from '../../lib/field-error';
import { isStockBusy } from '../../lib/refusal';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';

export type PolicyPreview = inferRouterOutputs<AppRouter>['restocks']['templatePreview'];
type Page = { vanOffset: number; rowOffset: number };
const firstPage: Page = { vanOffset: 0, rowOffset: 0 };
export const sourceLabel = (source: string | null | undefined) =>
  ({ template: 'Template', local: 'Local', van_only: 'Van only', by_hand: 'By hand' })[
    source ?? ''
  ] ?? 'Not watched';
const quantity = (n: number | null) => (n === null ? 'Unavailable' : formatQty(n));

/** A changed proposal needs a fresh first-page revision before restoring continuation. */
export async function fetchPolicyReview(
  request: (input: VanTemplatePreviewInput) => Promise<PolicyPreview>,
  proposal: VanTemplateProposal,
  page: Page,
  revision: string | undefined,
  stillCurrent: () => boolean,
  restorePage?: Page,
): Promise<{ view: PolicyPreview; page: Page } | null> {
  let view = await request({
    proposal,
    page,
    ...(revision ? { expectedPolicyRevision: revision } : {}),
  });
  if (!stillCurrent()) return null;
  if (restorePage && (restorePage.rowOffset > 0 || restorePage.vanOffset > 0)) {
    page = restorePage;
    view = await request({ proposal, page, expectedPolicyRevision: view.policyRevision });
    if (!stillCurrent()) return null;
  }
  return { view, page };
}

/** One captured proposal owns its preview pages and retry key. Late replies from an older
 * proposal/account cannot restore a preview, close a sheet, or invalidate another account. */
export function useTemplateReview(
  onSaved: () => void,
  onBusyChange?: (busy: boolean) => void,
) {
  const client = useTRPCClient();
  const trpc = useTRPC(),
    qc = useQueryClient(),
    session = useSession(),
    online = useOnline();
  const scope = `${session.orgId}:${session.userId}:${session.role}`,
    currentScope = useRef(scope);
  currentScope.current = scope;
  const alive = useRef(true),
    sequence = useRef(0),
    keys = useRef(createIntentKey());
  const [state, setState] = useState<{
    scope: string;
    proposal: VanTemplateProposal;
    page: Page;
    view?: PolicyPreview;
    loading: boolean;
    fresh: boolean;
    error?: Error;
  } | null>(null);
  const [draftError, setDraftError] = useState<Error | null>(null);
  const save = useMutation(trpc.restocks.templateCommit.mutationOptions());
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      sequence.current++;
    };
  }, []);
  useEffect(() => {
    sequence.current++;
    setState(null);
    keys.current.clear();
  }, [scope]);
  useEffect(() => {
    onBusyChange?.(save.isPending);
    return () => onBusyChange?.(false);
  }, [save.isPending, onBusyChange]);
  const valid = (s: string, n: number) =>
    alive.current && currentScope.current === s && sequence.current === n;
  const load = async (
    proposal: VanTemplateProposal,
    page: Page = firstPage,
    revision?: string,
    restorePage?: Page,
  ) => {
    if (save.isPending || !online || !can(session.role, 'inventory', 'manage')) return;
    const n = ++sequence.current;
    setDraftError(null);
    setState((old) => ({
      scope,
      proposal,
      page: restorePage ?? page,
      view: old?.scope === scope ? old.view : undefined,
      loading: true,
      fresh: false,
    }));
    try {
      const result = await fetchPolicyReview(
        (input) => client.restocks.templatePreview.query(input),
        proposal,
        page,
        revision,
        () => valid(scope, n),
        restorePage,
      );
      if (result && valid(scope, n))
        setState({ scope, proposal, ...result, loading: false, fresh: true });
    } catch (e) {
      if (valid(scope, n))
        setDraftError(e instanceof Error ? e : new Error('Could not review changes.'));
      if (valid(scope, n))
        setState(
          (old) =>
            old && {
              ...old,
              loading: false,
              fresh: false,
              error: e instanceof Error ? e : new Error('Could not review changes.'),
            },
        );
    }
  };
  const shown = state?.scope === scope ? state : null;
  const confirm = async () => {
    if (
      !shown?.view ||
      !shown.fresh ||
      shown.loading ||
      save.isPending ||
      !online ||
      !shown.view.canCommit ||
      !can(session.role, 'inventory', 'manage')
    )
      return;
    const n = sequence.current,
      payload = { proposal: shown.proposal, expectedRevision: shown.view.policyRevision };
    try {
      await save.mutateAsync({
        ...payload,
        ...keys.current.forIntent([scope, 'restocks.templateCommit', payload]),
      });
      if (!valid(scope, n)) return;
      keys.current.clear();
      await Promise.all(
        [
          trpc.restocks.pathKey(),
          trpc.inventory.reorderPoints.queryKey(),
          trpc.inventory.lowStock.queryKey(),
        ].map((queryKey) => qc.invalidateQueries({ queryKey })),
      );
      if (valid(scope, n)) {
        setState(null);
        onSaved();
      }
    } catch (e) {
      if (valid(scope, n))
        setState(
          (old) =>
            old && {
              ...old,
              error: e instanceof Error ? e : new Error('Could not save changes.'),
            },
        );
    }
  };
  return {
    state: shown,
    allowed: can(session.role, 'inventory', 'manage'),
    draftError,
    online,
    saving: save.isPending,
    start: load,
    confirm,
    back: () => {
      if (!save.isPending) {
        sequence.current++;
        setState(null);
      }
    },
    reload: () => {
      if (shown) void load(shown.proposal);
    },
    page: (page: Page) => {
      if (shown?.view) void load(shown.proposal, page, shown.view.policyRevision);
    },
    preserve: (itemId: string, keep: boolean) => {
      if (shown?.proposal.kind === 'apply') {
        const ids = new Set(shown.proposal.preserveLocalItemIds);
        if (keep) ids.add(itemId);
        else ids.delete(itemId);
        void load(
          { ...shown.proposal, preserveLocalItemIds: [...ids].sort() },
          firstPage,
          undefined,
          shown.page,
        );
      }
    },
  };
}

export function VanTemplatePreview({
  view,
  proposal,
  page,
  loading,
  saving,
  online,
  error,
  onConfirm,
  onPage,
  onPreserveLocal,
  onReload,
  onBack,
  authorized = true,
}: {
  authorized?: boolean;
  view?: PolicyPreview;
  proposal: VanTemplateProposal;
  page: Page;
  loading: boolean;
  saving: boolean;
  online: boolean;
  error?: Error | null;
  onConfirm: () => void;
  onPage: (page: Page) => void;
  onPreserveLocal: (itemId: string, keep: boolean) => void;
  onReload: () => void;
  onBack: () => void;
}) {
  const title = useRef<HTMLHeadingElement>(null),
    root = useRef<HTMLElement>(null),
    focusChoice = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && focusChoice.current) {
      const id = focusChoice.current;
      focusChoice.current = null;
      requestAnimationFrame(() =>
        root.current
          ?.querySelector<HTMLInputElement>(`[data-local-item="${id}"]`)
          ?.focus(),
      );
    }
  }, [loading, view?.policyRevision]);
  useEffect(() => {
    title.current?.focus();
  }, []);
  const disabled = saving || loading || !online || !authorized;
  const pair = (p: PolicyPreview['rows'][number]['after']) =>
    p ? (
      <>
        <strong>{sourceLabel(p.source)}</strong>
        <div>Keep at least {quantity(p.keepAtLeastHundredths)}</div>
        <div>Fill to {quantity(p.fillToHundredths)}</div>
      </>
    ) : (
      'Not watched'
    );
  const columns: DataTableColumn<PolicyPreview['rows'][number]>[] = [
    {
      key: 'part',
      label: 'Part and van',
      cell: (r) => (
        <div className="min-w-40 whitespace-normal">
          <strong className="block">{r.itemName}</strong>
          <span>
            {r.vanName} · {r.ownerName}
          </span>
        </div>
      ),
    },
    {
      key: 'before',
      label: 'Before',
      cell: (r) => (
        <div className="min-w-32">
          {pair(r.before)}
          <div>
            Purchase pack{' '}
            {r.packBeforeHundredths === null ? 'None' : quantity(r.packBeforeHundredths)}
          </div>
          <div>Suggested {quantity(r.suggestedBeforeHundredths)}</div>
        </div>
      ),
    },
    {
      key: 'after',
      label: 'After',
      cell: (r) => (
        <div className="min-w-32">
          {pair(r.after)}
          <div>
            Purchase pack{' '}
            {r.packAfterHundredths === null ? 'None' : quantity(r.packAfterHundredths)}
          </div>
          <div>Suggested {quantity(r.suggestedAfterHundredths)}</div>
        </div>
      ),
    },
    {
      key: 'stock',
      label: 'Quantity evidence',
      cell: (r) => (
        <div className="min-w-36">
          <div>
            {r.stockEvidence === 'retained' ? 'Recorded stock' : 'On hand'}{' '}
            {quantity(r.onHandHundredths)}
          </div>
          <div>On order {quantity(r.onOrderHundredths)}</div>
          <div>Owed {quantity(r.owedHundredths)}</div>
          {!r.planningAvailable ? <div>Unavailable for planning</div> : null}
        </div>
      ),
    },
    {
      key: 'choice',
      label: 'Overlap choice',
      cell: (r) => {
        const c = view?.conflicts.find(
          (c) => c.vanId === r.vanId && c.itemId === r.itemId,
        );
        return c && proposal.kind === 'apply' ? (
          <label className="flex min-h-11 min-w-32 items-center gap-2">
            <input
              type="checkbox"
              data-local-item={r.itemId}
              checked={c.preserved}
              disabled={disabled}
              onChange={(e) => {
                focusChoice.current = r.itemId;
                onPreserveLocal(r.itemId, e.target.checked);
              }}
            />
            Keep Local
          </label>
        ) : null;
      },
    },
  ];
  return (
    <section
      ref={root}
      className="space-y-4 [&_button]:min-h-11 [&_button]:min-w-11"
      aria-busy={loading || saving}
    >
      <h2 ref={title} tabIndex={-1} className="font-heading text-lg font-semibold">
        Review changes
      </h2>
      {!authorized ? (
        <Notice tone="danger" alert>
          Your role no longer permits policy changes. Your draft is kept.
        </Notice>
      ) : null}
      {!online ? (
        <Notice tone="warn">
          Offline. Your draft is kept; go online and reload the review.
        </Notice>
      ) : null}
      {error ? (
        <Notice tone="danger" alert>
          {fieldErrorOf(error.message)}
          {isStockBusy(error) ? ' This attempt made no changes. Try saving again.' : ''}
        </Notice>
      ) : null}
      {loading ? <p role="status">Reviewing changes…</p> : null}
      {view ? (
        <>
          <p className="text-sm">
            {view.totals.vans} vans · {view.totals.pairs} pairs · {view.totals.conflicts}{' '}
            overlaps
          </p>
          <p className="text-xs text-muted-foreground">
            Quantity evidence as of {view.asOf}. Purchase suggestions are estimates.
          </p>
          {view.blockers.length ? (
            <Notice tone="danger" alert>
              <ul>
                {view.blockers.map((b, i) => (
                  <li key={i}>{b.message}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {view.templateChange ? (
            <section className="space-y-2 border border-border p-3 text-sm">
              <h3 className="font-semibold">
                Template: {view.templateChange.nameBefore} →{' '}
                {view.templateChange.nameAfter ?? 'Deleted'}
              </h3>
              <p>{view.templateChange.total} template pairs</p>
              <ul className="divide-y divide-border">
                {view.templateChange.points.map((p) => (
                  <li key={p.itemId} className="space-y-1 py-2">
                    <strong className="block break-words">{p.itemName}</strong>
                    <div>
                      Before:{' '}
                      {p.before
                        ? `Keep at least ${quantity(p.before.keepAtLeastHundredths)} · Fill to ${quantity(p.before.fillToHundredths)}`
                        : 'Not in template'}
                    </div>
                    <div>
                      After:{' '}
                      {p.after
                        ? `Keep at least ${quantity(p.after.keepAtLeastHundredths)} · Fill to ${quantity(p.after.fillToHundredths)}`
                        : 'Removed from template'}
                    </div>
                  </li>
                ))}
              </ul>
              {view.templateChange.total > 100 ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    disabled={disabled || page.rowOffset === 0}
                    onClick={() =>
                      onPage({ ...page, rowOffset: Math.max(0, page.rowOffset - 100) })
                    }
                  >
                    Previous template pairs
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={disabled || view.templateChange.nextOffset === null}
                    onClick={() =>
                      onPage({ ...page, rowOffset: view.templateChange!.nextOffset! })
                    }
                  >
                    Next template pairs
                  </Button>
                </div>
              ) : null}
            </section>
          ) : null}
          <ul className="space-y-2 text-sm">
            {view.vans.map((v) => (
              <li key={v.vanId}>
                <strong>{v.name}</strong> · {v.ownerName}
                <div>
                  Feeder: {v.feederBeforeName ?? 'None'} → {v.feederAfterName ?? 'None'}
                </div>
              </li>
            ))}
          </ul>
          {view.totals.vans > 25 ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                disabled={disabled || page.vanOffset === 0}
                onClick={() =>
                  onPage({ ...page, vanOffset: Math.max(0, page.vanOffset - 25) })
                }
              >
                Previous vans
              </Button>
              <Button
                variant="ghost"
                disabled={disabled || view.nextVanOffset === null}
                onClick={() => onPage({ ...page, vanOffset: view.nextVanOffset! })}
              >
                Next vans
              </Button>
            </div>
          ) : null}
          <DataTable
            columns={columns}
            rows={view.rows}
            rowKey={(r) => `${r.vanId}:${r.itemId}`}
            empty="No quantity rows for this change."
            server={{
              total: view.totals.pairs,
              page: Math.floor(page.rowOffset / 100),
              pageSize: 100,
              onPage: (n) => onPage({ ...page, rowOffset: n * 100 }),
              sort: null,
              onSort: () => {},
              loading,
            }}
          />
          <p className="text-sm">{view.retention}</p>
          {!view.hasChanges ? <p>No policy changes to save.</p> : null}
        </>
      ) : null}
      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        <Button variant="ghost" disabled={saving} onClick={onBack}>
          Back to draft
        </Button>
        <Button variant="ghost" disabled={disabled} onClick={onReload}>
          Reload review
        </Button>
        <Button
          className="min-h-11"
          disabled={disabled || !view?.canCommit || (!!error && !isStockBusy(error))}
          onClick={onConfirm}
        >
          {saving
            ? 'Saving…'
            : isStockBusy(error)
              ? 'Try saving again'
              : 'Confirm changes'}
        </Button>
      </div>
    </section>
  );
}
